/**
 * Global feature flag for the Lumio-managed acquisition channels.
 *
 * The first release ships with **Web only**; App / Crypto / Short-Videos are wired
 * end-to-end but gated off here, shown across the app as frosted "Unlocking soon"
 * cards (see `components/LockVeil`). Flip a channel's `enabled` flag below and the
 * whole app reacts in one place — the daily-budget allocator starts funding it, its
 * cards unlock and become navigable, and its detail page stops redirecting.
 *
 * This is the SINGLE SOURCE OF TRUTH. Every surface that locks/unlocks a channel must
 * derive from {@link isAcquisitionChannelEnabled} (or {@link enabledAcquisitionAfts}),
 * never hard-code its own list. React surfaces use {@link useAcquisitionChannelGate} so
 * they re-render the instant the runtime reveal flag (below) flips.
 *
 * The four channels are keyed three different ways across the app, so the registry
 * records all of them and the helper normalises any of them:
 *  - `aft`       — the API/stats/budget literal (`web`/`app`/`crypto`/`video`); also the
 *                  `BUDGET_CHANNEL_IDS` used by the daily-limit allocator.
 *  - `channelId` — the channel-detail / Lumio-grid / route id (`web-networks`, …).
 *  - `homeId`    — the Home "Acquisition Channels" grid id (`web`/`apps`/`crypto`/`short`).
 *
 * Self-managed channels (Affiliates, Influencers) are a separate subsystem — their figures
 * come from the Stats `daily_affiliate_activity` set, not the daily-budget allocator, so they
 * are NOT part of {@link ALL_ACQUISITION_AFTS} and never enter `budget_details`. They ARE gated
 * the same way, by their own {@link SELF_MANAGED_REGISTRY} below: locked by default, shown as
 * frosted "Unlocking soon" cards, revealed in one flip by the runtime reveal flag. Any id in
 * NEITHER registry resolves to enabled, so unrelated cards are never accidentally locked.
 *
 * Runtime reveal (no code change / redeploy): setting the `show_in_progress` localStorage
 * flag to `'true'` (see `hooks/useShowInProgress`) unlocks EVERY gated channel at runtime,
 * so QA can preview the not-yet-shipped channels live. It takes precedence over the release
 * defaults and the `window.config` override below, and is read fresh on every call so a
 * devtools flip is honoured immediately; React surfaces stay in sync via
 * {@link useAcquisitionChannelGate} (it re-renders on the flip).
 *
 * Per-environment runtime control: the resolver also honours an optional
 * `window.config.acquisitionChannelsEnabled` override (a comma-separated `aft` list, e.g.
 * `"web,app"`). It is intentionally NOT registered in the deploy Helm `config.yaml` yet —
 * wire that key there to make the set per-environment configurable; until then the code
 * defaults below win.
 */
import { SHOW_IN_PROGRESS_KEY } from '../hooks/useShowInProgress';
import type { ChannelId } from '../mock-data/types';
import type { AffiliateType } from '../pages/home/stats-mapping';

/** The four Lumio-managed acquisition channels, keyed by their API `aft` literal. */
export type AcquisitionChannelKey = Extract<AffiliateType, 'web' | 'app' | 'crypto' | 'video'>;

type ChannelRegistryEntry = {
  /** API/stats/budget literal — the canonical id (shared with `BUDGET_CHANNEL_IDS`). */
  aft: AcquisitionChannelKey;
  /** Channel-detail / Lumio-grid / route id. */
  channelId: ChannelId;
  /** Home "Acquisition Channels" grid id. */
  homeId: string;
  /** Whether the channel is live in this release. */
  enabled: boolean;
};

/**
 * The release default. Flip `enabled` to roll a channel out; nothing else changes.
 */
const REGISTRY: readonly ChannelRegistryEntry[] = [
  { aft: 'web', channelId: 'web-networks', homeId: 'web', enabled: true },
  { aft: 'app', channelId: 'app-networks', homeId: 'apps', enabled: false },
  { aft: 'crypto', channelId: 'crypto-sites', homeId: 'crypto', enabled: false },
  { aft: 'video', channelId: 'short-videos', homeId: 'short', enabled: false },
];

/** Every key scheme → its registry entry, so the helper accepts any of them. */
const ENTRY_BY_KEY = new Map<string, ChannelRegistryEntry>();
REGISTRY.forEach((entry) => {
  ENTRY_BY_KEY.set(entry.aft, entry);
  ENTRY_BY_KEY.set(entry.channelId, entry);
  ENTRY_BY_KEY.set(entry.homeId, entry);
});

/**
 * Whether the runtime `show_in_progress` reveal flag is on (see `hooks/useShowInProgress`).
 * When set, every gated channel is treated as live so QA can preview the not-yet-shipped
 * channels without a redeploy. Read fresh on every call (never cached) so a devtools flip is
 * honoured immediately; React surfaces re-render via {@link useAcquisitionChannelGate}.
 */
const revealAllChannels = (): boolean => {
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem(SHOW_IN_PROGRESS_KEY) === 'true';
  } catch {
    // Private mode / blocked storage → keep the release defaults.
    return false;
  }
};

/**
 * Resolve the live set of `aft`s, fresh on every call so runtime flips are honoured:
 *  1. the `show_in_progress` reveal flag unlocks every channel (highest precedence);
 *  2. an optional `window.config.acquisitionChannelsEnabled` (comma-separated `aft` list)
 *     overrides the set per-environment from the deploy config;
 *  3. otherwise the registry's `enabled` flags (the release default) win.
 */
const resolveEnabledAfts = (): ReadonlySet<AcquisitionChannelKey> => {
  if (revealAllChannels()) return new Set(REGISTRY.map((entry) => entry.aft));

  const raw =
    typeof window !== 'undefined'
      ? (window.config as unknown as { acquisitionChannelsEnabled?: string } | undefined)
          ?.acquisitionChannelsEnabled
      : undefined;

  if (typeof raw === 'string' && raw.trim() !== '') {
    const wanted = new Set(raw.split(',').map((part) => part.trim()));
    return new Set(REGISTRY.filter((entry) => wanted.has(entry.aft)).map((entry) => entry.aft));
  }
  return new Set(REGISTRY.filter((entry) => entry.enabled).map((entry) => entry.aft));
};

/**
 * The self-managed (non-Lumio) acquisition channels, keyed by their UI / route id
 * (`affiliates` / `influencers`). Gated exactly like the Lumio channels above — locked by
 * default, frosted "Unlocking soon", flipped live by the runtime reveal flag — but tracked in
 * their OWN registry so they stay out of {@link ALL_ACQUISITION_AFTS} and the `budget_details`
 * the daily-limit allocator persists. Flip `enabled` to ship one.
 */
export type SelfManagedChannelId = 'affiliates' | 'influencers';

const SELF_MANAGED_REGISTRY: readonly { id: SelfManagedChannelId; enabled: boolean }[] = [
  // The Affiliates entrypoint (the acquisition card and the /account/marketing/affiliates route).
  // GENERALLY AVAILABLE again, together with its tabbed management (`affiliate-management` in
  // config/featureFlags). Rollback switch: `enabled: false` frosts the card ("Unlocking soon" +
  // padlock) and redirects the route (AffiliatesRoute), with the "777" reveal (show_in_progress)
  // still unlocking it for internal QA.
  { id: 'affiliates', enabled: true },
  { id: 'influencers', enabled: false },
];

/** Which ids this self-managed registry governs (fast membership test). */
const SELF_MANAGED_IDS = new Set<string>(SELF_MANAGED_REGISTRY.map((entry) => entry.id));

/**
 * Resolve the live set of self-managed channel ids, fresh on every call — mirrors
 * {@link resolveEnabledAfts}: the `show_in_progress` reveal flag unlocks every one of them
 * (highest precedence), then an optional `window.config.selfManagedChannelsEnabled`
 * (comma-separated id list) overrides the set per-environment, otherwise the registry's
 * `enabled` flags (the release default) win.
 */
const resolveEnabledSelfManaged = (): ReadonlySet<SelfManagedChannelId> => {
  if (revealAllChannels()) return new Set(SELF_MANAGED_REGISTRY.map((entry) => entry.id));

  const raw =
    typeof window !== 'undefined'
      ? (window.config as unknown as { selfManagedChannelsEnabled?: string } | undefined)
          ?.selfManagedChannelsEnabled
      : undefined;

  if (typeof raw === 'string' && raw.trim() !== '') {
    const wanted = new Set(raw.split(',').map((part) => part.trim()));
    return new Set(SELF_MANAGED_REGISTRY.filter((entry) => wanted.has(entry.id)).map((entry) => entry.id));
  }
  return new Set(SELF_MANAGED_REGISTRY.filter((entry) => entry.enabled).map((entry) => entry.id));
};

/** Every acquisition channel `aft` literal, in registry order, regardless of the
 *  `enabled` flag — the canonical set the budget allocator persists under
 *  `budget_details`. Derive lists from this so registering a new channel in
 *  `REGISTRY` flows through automatically (no parallel hardcoded copy to drift). */
export const ALL_ACQUISITION_AFTS: readonly AcquisitionChannelKey[] = REGISTRY.map((entry) => entry.aft);

/** The live channels' `aft` literals, in registry order. Resolved on each call (honours the
 *  runtime reveal flag + `window.config` override), so it is a function, not a constant. */
export const enabledAcquisitionAfts = (): readonly AcquisitionChannelKey[] => {
  const enabled = resolveEnabledAfts();
  return REGISTRY.filter((entry) => enabled.has(entry.aft)).map((entry) => entry.aft);
};

/**
 * Whether a channel is live in this release. Accepts ANY of the three Lumio key schemes
 * (`aft`, `channelId`, or `homeId`) and the self-managed ids (`affiliates` / `influencers`),
 * each resolved against its own registry. Ids in NEITHER registry — anything non-acquisition —
 * are NOT governed and resolve to `true`, so unrelated cards are never accidentally locked.
 * Resolves at call time (honours the runtime reveal flag); React callers wrap it in
 * {@link useAcquisitionChannelGate} to re-render on a flip.
 */
export const isAcquisitionChannelEnabled = (key: string | undefined): boolean => {
  if (!key) return true;
  const entry = ENTRY_BY_KEY.get(key);
  if (entry) return resolveEnabledAfts().has(entry.aft);
  if (SELF_MANAGED_IDS.has(key)) return resolveEnabledSelfManaged().has(key as SelfManagedChannelId);
  return true;
};

/** Whether a channel is gated off in this release (inverse of the above). */
export const isAcquisitionChannelLocked = (key: string | undefined): boolean =>
  !isAcquisitionChannelEnabled(key);

/**
 * Resolve the API `aft` literal (`web` / `app` / `crypto` / `video`) for ANY channel key
 * (`aft`, `channelId`, or `homeId`) — the canonical id for the stats / budget / ad-service
 * `network`. Returns `undefined` for a non-acquisition id. Derive from this rather than a
 * parallel hardcoded map, so registering a channel flows through in one place.
 */
export const acquisitionAftFor = (key: string | undefined): AcquisitionChannelKey | undefined =>
  key ? ENTRY_BY_KEY.get(key)?.aft : undefined;
